import type { ThemeColorsMeta } from '@frameio/vapor';

// Type definitions -----------------------------------------------------------
export type MetaToken = Exclude<ThemeColorsMeta, 'lime' | 'aqua'>;
export type HexColor = `#${string}`;
export type VaporHex =
  (typeof vaporColorMapping)[keyof typeof vaporColorMapping];

// Color mappings -------------------------------------------------------------

/** Vapor design system color tokens mapped to their hex values */
export const vaporColorMapping = {
  yellow: '#EEB500',
  coral: '#FE7154',
  orange: '#FE6F03',
  red: '#FE6470',
  maroon: '#FE56A6',
  pink: '#FC55F7',
  purple: '#AA8AFF',
  blue: '#739FFF',
  teal: '#00BFC0',
  green: '#03C858',
  default: '#7E8098',
} satisfies Record<MetaToken, HexColor>;

/** Backend color hex values mapped to their corresponding token names */
export const backendColorMapping = {
  yellow: '#f0cd33',
  coral: '#fc856d',
  orange: '#f27637',
  red: '#eb4747',
  maroon: '#f04b96',
  pink: '#fd77f6',
  purple: '#8952fd',
  blue: '#4979fa',
  teal: '#47ebeb',
  green: '#21c17b',
  default: '#7d8198',
} satisfies Record<MetaToken, HexColor>;

/**
 * Additional color mappings for legacy or custom colors that don't fit into
 * the standard backend or vapor mappings. Maps hex values to token names.
 *
 * Add new colors here when they need special handling.
 */
export const additionalColorMappings: Record<string, MetaToken> = {
  '#00b564': 'green', // Custom green variant
  '#009cff': 'blue', // Custom blue variant
  '#eeb500': 'yellow', // Vapor yellow in backend
  '#fe6470': 'red', // Vapor red in backend
};

// Derived mappings -----------------------------------------------------------

/** Inverse map of backend hex codes to tokens for quick lookups */
export const hexToTokenFromBackend = Object.fromEntries(
  Object.entries(backendColorMapping).map(([token, hex]) => [
    hex.toLowerCase(),
    token,
  ])
) as Record<HexColor, MetaToken>;

/** Inverse map of additional hex codes to tokens for quick lookups */
export const hexToTokenFromAdditional = Object.fromEntries(
  Object.entries(additionalColorMappings).map(([hex, token]) => [
    hex.toLowerCase(),
    token,
  ])
) as Record<HexColor, MetaToken>;

// Helper functions -----------------------------------------------------------

/** Checks if a string is a valid color token in our system */
export function isValidColorToken(token: string): token is MetaToken {
  return token in vaporColorMapping;
}

/** Checks if a hex color is recognized in our backend mapping */
export function isValidHexColor(hex: string): hex is HexColor {
  return hex.toLowerCase() in hexToTokenFromBackend;
}

/** Checks if a hex color is recognized in our additional mapping */
export function isAdditionalHexColor(hex: string): hex is HexColor {
  return hex.toLowerCase() in hexToTokenFromAdditional;
}

// Color conversion functions -------------------------------------------------

/**
 * Resolves any color input (hex or token name) to a validated token name.
 *
 * Resolution order:
 * 1. Check if it's in additional color mappings
 * 2. If it's a hex, check backend mappings
 * 3. If it's a hex, check vapor mappings
 * 4. If it's a token name, validate it
 * 5. Default to 'default' if no match found
 */
export const getValidatedToken = (colorInput: string): MetaToken => {
  if (!colorInput) return 'default';

  const normalizedInput = colorInput.toLowerCase();

  // If it's a hex color
  if (normalizedInput.startsWith('#')) {
    // Check additional mappings first
    if (isAdditionalHexColor(normalizedInput)) {
      return hexToTokenFromAdditional[normalizedInput];
    }

    // Then check backend mappings
    if (isValidHexColor(normalizedInput)) {
      return hexToTokenFromBackend[normalizedInput];
    }

    // Try to find in vapor mappings
    const vaporToken = Object.keys(vaporColorMapping).find(
      (token) =>
        vaporColorMapping[token as MetaToken].toLowerCase() === normalizedInput
    ) as MetaToken | undefined;

    return vaporToken || 'default';
  }

  // If it's a token name
  return isValidColorToken(normalizedInput) ? normalizedInput : 'default';
};

/**
 * Resolves any color input (hex or token name) to a validated Vapor hex value.
 * Always returns a hex value from the Vapor design system.
 */
export const getValidatedHex = (colorInput: string): VaporHex => {
  if (!colorInput) return vaporColorMapping.default;

  // If it's already a hex, convert it to a token first to ensure it maps to a valid vapor color
  const token = getValidatedToken(colorInput);
  return vaporColorMapping[token];
};
